import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTimes,
  faChevronDown,
  faChevronUp,
  faCalendarAlt,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";

// Выносим стили за пределы компонента
const ribbonStyles = `
  .ribbon-3d {
    position: absolute;
    top: -5px;
    right: -5px;
    z-index: 10;
    width: 100px;
    height: 100px;
    overflow: hidden;
    pointer-events: none;
  }
  .ribbon-3d-inner {
    position: absolute;
    display: block;
    width: 150px;
    padding: 8px 0;
    background-color: #006674;
    box-shadow: 0 3px 6px rgba(0,0,0,0.3);
    color: white;
    font-size: 12px;
    font-weight: bold;
    text-transform: uppercase;
    text-align: center;
    left: -15px;
    top: 19px;
    transform: rotate(45deg);
    letter-spacing: 1px;
    border: 1px solid rgba(255,255,255,0.2);
  }
  .ribbon-3d::before {
    content: '';
    position: absolute;
    z-index: -1;
    border: 3px solid #a16207;
    border-top-color: transparent;
    border-right-color: transparent;
    top: 0;
    right: 83px;
  }
  .ribbon-3d::after {
    content: '';
    position: absolute;
    z-index: -1;
    border: 3px solid #a16207;
    border-top-color: transparent;
    border-left-color: transparent;
    top: 83px;
    right: 0;
  }
`;

const TopCandidates = () => {
  const { t, i18n } = useTranslation();

  const [candidates, setCandidates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSphere, setSelectedSphere] = useState(null);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [spheres, setSpheres] = useState([]);
  const [sphereDropdownOpen, setSphereDropdownOpen] = useState(false);
  const [sphereSearchTerm, setSphereSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const dropdownRef = useRef(null);
  const isMounted = useRef(true);

  // Мемоизируем фильтры
  const filters = useMemo(() => {
    const filtres = t("filtres", { returnObjects: true });
    if (Array.isArray(filtres)) {
      return filtres;
    }
    if (typeof filtres === "object" && filtres !== null) {
      return Object.values(filtres);
    }
    return [];
  }, [t]);

  // Мемоизируем sphereMap
  const sphereMap = useMemo(() => {
    const map = {};
    filters.forEach((name, i) => {
      map[name] = `sphere_${i}`;
    });
    return map;
  }, [filters]);

  // Загрузка кандидатов
  useEffect(() => {
    isMounted.current = true;

    const fetchCandidates = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/top_candidates.php`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Fetched data:", data);

        if (isMounted.current) {
          if (Array.isArray(data) && data.length > 0) {
            const normalizedCandidates = data.map((candidate) => ({
              ...candidate,
              extra_info:
                typeof candidate.extra_info === "string"
                  ? JSON.parse(candidate.extra_info || "{}")
                  : candidate.extra_info || {},
              name: candidate.name || "",
              lastName: candidate.lastName || "",
              id: candidate.id,
              photo: candidate.photo || candidate.companyLogo || null,
              created_at: candidate.created_at || new Date().toISOString(),
            }));

            setCandidates(normalizedCandidates);
            setFilteredCandidates(normalizedCandidates);
            setSpheres(filters);
          } else {
            setCandidates([]);
            setFilteredCandidates([]);
            setSpheres(filters);
          }
        }
      } catch (err) {
        console.error("Ошибка загрузки кандидатов:", err);
        if (isMounted.current) {
          setError(err.message);
          setCandidates([]);
          setFilteredCandidates([]);
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    fetchCandidates();

    return () => {
      isMounted.current = false;
    };
  }, [filters]);

  // Фильтрация кандидатов
  useEffect(() => {
    if (candidates.length === 0) {
      setFilteredCandidates([]);
      return;
    }

    const filtered = candidates.filter((candidate) => {
      const fullName =
        `${candidate.name || ""} ${candidate.lastName || ""}`.toLowerCase();
      const matchesName = fullName.includes(searchTerm.toLowerCase());
      const matchesId = candidate.id?.toString().includes(searchTerm);

      const extraInfo = candidate.extra_info || {};
      const mainSphere = extraInfo.mainSphere || null;
      const additionalSphere = extraInfo.additionalSphere || null;

      const matchesSphere =
        !selectedSphere ||
        sphereMap[selectedSphere] === mainSphere ||
        sphereMap[selectedSphere] === additionalSphere;

      return (matchesName || matchesId) && matchesSphere;
    });

    setFilteredCandidates(filtered);
  }, [searchTerm, selectedSphere, candidates, sphereMap]);

  const handleClear = () => {
    setSearchTerm("");
    setSelectedSphere(null);
  };

  const options = useMemo(
    () => [
      { key: "offer_services", label: t("offerservices") },
      { key: "search_work", label: t("searchworktitle") },
    ],
    [t],
  );

  const handleSphereSelect = (sphere) => {
    setSelectedSphere((prev) => (prev === sphere ? null : sphere));
    setSphereDropdownOpen(false);
  };

  const toggleDropdown = () => setSphereDropdownOpen((prev) => !prev);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSphereDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredSpheres = spheres.filter((sphere) =>
    sphere.toLowerCase().includes(sphereSearchTerm.toLowerCase()),
  );

  const getSphereLabel = (sphereKey) => {
    if (!sphereKey) return null;
    return (
      Object.keys(sphereMap).find((key) => sphereMap[key] === sphereKey) || null
    );
  };

  // Показываем загрузку только первый раз
  if (isLoading && candidates.length === 0) {
    return (
      <section className="min-h-screen">
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              className="text-white text-4xl mb-4"
            />
            <p className="text-white text-lg">{t("loading") || "Բեռնում..."}</p>
          </div>
        </div>
      </section>
    );
  }

  // Показываем ошибку
  if (error && candidates.length === 0) {
    return (
      <section className="min-h-screen">
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <p className="text-red-500 text-lg mb-2">
              {t("error") || "Սխալ տվյալների բեռնման ժամանակ"}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-[#006674] text-white rounded-lg hover:bg-[#004c55]"
            >
              {t("retry") || "Կրկնել"}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen">
      {/* Добавляем стили один раз */}
      <style>{ribbonStyles}</style>

      <div className="flex w-full max-md:flex-col justify-between items-start p-6 mb-6 gap-4">
        <h1 className="text-4xl max-md:text-2xl max-sm:text-xl font-bold uppercase text-white">
          {t("TOPcandidates") || "Լավագույն թեկնածուներ"}
        </h1>

        <div className="flex flex-col gap-3 max-md:w-full w-max">
          <div className="flex items-center bg-white rounded-full pr-2 py-1 shadow-lg">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("search_placeholder") || "Փնտրել թեկնածու / ID..."}
              className="py-2 px-6 outline-none text-lg rounded-l-full w-full"
            />
            {searchTerm && (
              <button
                onClick={handleClear}
                className="text-gray-500 hover:text-red-500 px-2 text-xl"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
            <button className="min-w-[45px] min-h-[45px] flex justify-center items-center text-white bg-[#006674] hover:bg-[#005560] transition-all text-xl rounded-full">
              <FontAwesomeIcon icon={faSearch} />
            </button>
          </div>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={toggleDropdown}
              className="w-full bg-white px-4 py-2 rounded-full flex justify-between items-center border border-gray-300 text-gray-700"
            >
              {selectedSphere || t("industry") || "Ոլորտ"}
              <FontAwesomeIcon
                icon={sphereDropdownOpen ? faChevronUp : faChevronDown}
              />
            </button>
            {sphereDropdownOpen && (
              <div className="absolute z-50 w-full bg-white border border-gray-300 mt-1 rounded-lg max-h-60 overflow-y-auto shadow-lg">
                <input
                  type="text"
                  value={sphereSearchTerm}
                  onChange={(e) => setSphereSearchTerm(e.target.value)}
                  placeholder={t("search_placeholder") || "Փնտրել ոլորտը..."}
                  className="w-full px-4 py-2 border-b outline-none"
                />
                <div className="flex flex-col">
                  {filteredSpheres.length > 0 ? (
                    filteredSpheres.map((sphere, index) => (
                      <button
                        key={index}
                        onClick={() => handleSphereSelect(sphere)}
                        className={`text-left px-4 py-2 hover:bg-gray-100 transition ${
                          selectedSphere === sphere
                            ? "bg-[#006674] text-white"
                            : ""
                        }`}
                      >
                        {sphere}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-gray-500">
                      {t("noresults") || "Արդյունքներ չկան"}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {filteredCandidates.length === 0 && !isLoading ? (
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <p className="text-white text-lg mb-4">
              {searchTerm || selectedSphere
                ? t("noresults") ||
                  "Ձեր հարցման համաձայն արդյունքներ չեն գտնվել։ Փորձեք օգտագործել այլ հիմնաբառեր կամ փոխել որոնման ֆիլտրերը:"
                : t("nocandidates") || "Թեկնածուներ չկան"}
            </p>
            {(searchTerm || selectedSphere) && (
              <button
                onClick={handleClear}
                className="px-4 py-2 bg-[#006674] text-white rounded-lg hover:bg-[#004c55] transition-colors"
              >
                {t("clearfilters") || "Մաքրել ֆիլտրերը"}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="w-full flex flex-wrap justify-start p-6 max-md:p-3 gap-6">
          {filteredCandidates.map((candidate) => {
            const extraInfo = candidate.extra_info || {};
            const serviceType = extraInfo.serviceType || extraInfo.service;
            const mainSphere = extraInfo.mainSphere;
            const additionalSphere = extraInfo.additionalSphere;
            const sphereLabel =
              getSphereLabel(mainSphere) || getSphereLabel(additionalSphere);

            return (
              <article
                key={candidate.id}
                className="relative w-[410px] max-md:w-full bg-white rounded-xl  shadow-lg hover:shadow-2xl transition-all duration-300 block"
              >
                {/* Ленточка */}
                <div className="ribbon-3d">
                  <div className="ribbon-3d-inner">ԹՈՓ</div>
                </div>

                <div className="p-4">
                  <div className="flex items-start ov gap-4">
                    <div className="relative">
                      <Link to={`/ViewCandidatePage/${candidate.id}`}>
                        <img
                          src={
                            candidate.photo
                              ? `/api/uploads/${candidate.photo}`
                              : "https://cdn-icons-png.flaticon.com/512/847/847969.png"
                          }
                          alt={`${candidate.name} ${candidate.lastName}`}
                          className="w-[100px] h-[100px] bg-gray-300 rounded-xl object-cover"
                        />
                      </Link>
                    </div>

                    <div className="flex-1 ">
                      <h2 className="text-xl max-sm:text-xl font-semibold text-[#006674] mb-2">
                        {candidate.name} {candidate.lastName}
                      </h2>

                      {serviceType && (
                        <span className="text-sm font-medium text-gray-600 block mb-1">
                          {options.find((opt) => opt.key === serviceType)
                            ?.label || serviceType}
                          {i18n.language === "am" ? "`" : ":"}
                        </span>
                      )}

                      {sphereLabel && (
                        <span className="text-sm text-gray-500">
                          {sphereLabel}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="w-full rounded-b-lg p-2 bg-[#006674] text-white flex justify-between items-center">
                  <span>
                    <FontAwesomeIcon className="mr-1" icon={faCalendarAlt} />
                    {new Date(candidate.created_at).toLocaleDateString()}
                  </span>
                  <Link to={`/ViewCandidatePage/${candidate.id}`}>
                    <span className="hover:underline">
                      {t("seemore") || "Մանրամասն"}
                    </span>
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default TopCandidates;
